/**
 * Base Template - Used for International Cities (LATAM, OTHER regions)
 * This preserves the current clean design for all non-US markets
 */

import { BRAND_NAME, BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/useSiteSettings";

import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";
// Amplitude tracking removed to prevent React error #310
import {
  trackTemplateUsageGA,
} from "@/lib/analytics";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
} from "@/lib/instagramBrowserDetection";
import { trackBaseTemplateClick } from "@/lib/baseTemplateAnalytics";
import {
  buildHeroCtaFlagsSnapshot,
  buildHeroCtaSessionContext,
  installHeroCtaEngagementWatch,
  postHeroCtaSlackFunnel,
  resolveActiveHeroCtaType,
  uninstallHeroCtaEngagementWatch,
} from "@/lib/heroCtaFunnel";
import {
  isExternalHeroCtaHref,
  resolveBirthdayHeroHref,
  resolveCheckoutHeroHref,
  resolveCommunityHeroHref,
  resolveHeroCtaAriaLabel,
  resolveHeroCtaDisplayLabel,
  resolveHeroCtaHrefForKind,
} from "@/lib/heroCtaAdminMap";
import {
  trackMejoresAmigosCtaClick,
  trackMejoresAmigosCtaImpression,
} from "@/lib/mejoresAmigosCtaAnalytics";
import {
  trackLightboxClosedPostHog,
  trackLightboxNavigationPostHog,
  trackLightboxOpenedPostHog,
  trackMediaItemClickPostHog,
  trackTemplateUsagePostHog,
  getOrCreateTabSessionId,
  POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY,
  registerBaseTemplateSessionContextPostHog,
  unregisterBaseTemplateSessionContextPostHog,
  trackBaseTemplateFunnelSessionPostHog,
} from "@/lib/posthogAnalytics";
import { trackViewContent } from "@/lib/facebookPixel";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useFreshCtaFlags } from "@/hooks/useFreshCtaFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useImageProtection } from "@/hooks/useImageProtection";
import {
  resolveBaseTemplateGalleryPhotos,
  type BaseTemplateGalleryMediaItem,
} from "@/lib/baseTemplateGalleryFromSiteSettings";
import {
  resolveBaseTemplateSiteContent,
  toCloudinaryLogoDelivery,
} from "@/lib/baseTemplateSiteContent";
import { resolveHeroCtaForBaseTemplate } from "@/lib/baseTemplateHeroCta";
import { BaseTemplateHeroCtaMembershipMux } from "@/components/templates/BaseTemplateHeroCtaMembershipMux";
import type { ResolvedHeroCtaOverride } from "@/lib/resolveHeroCtaFromSiteSettings";
import {
  getCloudinaryUrl,
  isCloudinaryWatermarkConfigured,
  resolveLogoUrl,
} from "@/lib/cloudinaryUtils";
import {
  getServerTemplateConfig,
  workerCtaNeedsDefensiveFetch,
  workerHtmlHasPositiveHeroCta,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";
import { getMercadoPagoCheckoutVipArUrl } from "@/lib/mercadopagoArCheckoutUrl";
import { useMercadoPagoInstagramEscape } from "@/hooks/useMercadoPagoInstagramEscape";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BaseTemplateFooter } from "@/components/templates/BaseTemplateFooter";
import { BaseTemplateTallyContact } from "@/components/templates/BaseTemplateTallyContact";
import {
  baseTemplateDeferredUi,
  type BaseTemplateDeferredUiTone,
} from "@/lib/baseTemplateDarkShell";
declare global {
  interface Window {
    __TEMPLATE_CONFIG__?: Partial<ServerTemplateConfig>;
  }
}

export interface TemplateProps {
  /** @deprecated La galería home usa solo site-settings; se mantiene por compatibilidad de páginas demo. */
  mediaItems: Array<{
    id: number;
    type: "video" | "photo";
    src: string;
    title: string;
    playbackId?: string;
    actionButton?: unknown;
  }>;
  /** @deprecated La galería usa solo fotos Cloudinary (site-settings). Ignorado. */
  skipMuxApiFilter?: boolean;
  // Optional: URL for header logo/name link (managed from admin)
  headerUrl?: string;
  // Optional: Logo image URL (Cloudinary, Cloudflare, etc.) - managed from admin
  logoUrl?: string;
  // Optional: Hero title and description (from admin site-settings)
  heroTitle?: string;
  heroDescription?: string;
  // Optional: Banner/hero background image (URL o public_id). Si no se pasa, usa Cloudflare por defecto.
  bannerImageUrl?: string;
  /** Cuando true, no mostrar img del banner hasta tener la URL final (evita flash Cloudflare → site settings) */
  bannerDeferred?: boolean;
  /** Cuando true, no mostrar galería hasta tener datos de site settings (evita carga escalonada) */
  galleryDeferred?: boolean;
  /** Texto "Sobre mí" (página /sobre-mi). Versión corta en home, completa en /sobre-mi */
  sobreMi?: string;
  /** Si es false, oculta el bloque corto en esta vista (p. ej. inicio); /sobre-mi no se ve afectado */
  showSobreMiOnHome?: boolean;
  /** Botón CTA extra en el hero (debajo del broadcast de WhatsApp). Solo se muestra si se pasa. */
  heroCta?: React.ReactNode;
  /** false = ocultar enlace /portfolio en el nav (sin portfolio público). Por defecto true. */
  showPortfolioNavLink?: boolean;
  /** false = ocultar enlace #portfolio (gallery scroll) del nav. */
  showGalleryNavLink?: boolean;
  /** false = ocultar enlace #contacto del nav (sin form de contacto). Por defecto true. */
  showContactNavLink?: boolean;
  /** false = ocultar enlace /sobre-mi del footer. Por defecto true. */
  showSobreMi?: boolean;
  brandName?: string;
  footerTagline?: string;
  /** false = ocultar sección Mi galería (#portfolio). Por defecto true. */
  showGallerySection?: boolean;
  /** false = ocultar formulario Tally (#contacto). Por defecto true. */
  showTallySection?: boolean;
}

const WHATSAPP_BROADCAST_URL = "/whatsapp-broadcast";

export const BaseTemplate: React.FC<TemplateProps> = ({
  mediaItems: _mediaItems,
  skipMuxApiFilter: _skipMuxApiFilter = true,
  headerUrl = "/",
  logoUrl,
  heroTitle,
  heroDescription,
  bannerImageUrl,
  bannerDeferred = false,
  galleryDeferred = false,
  sobreMi,
  showSobreMiOnHome,
  heroCta,
  showPortfolioNavLink = true,
  showGalleryNavLink,
  showContactNavLink = true,
  showSobreMi = false,
  brandName = BRAND_NAME,
  footerTagline,
  showGallerySection,
  showTallySection,
}) => {
  // Site-settings: Worker KV → /api/site-settings → props (Index u otras landings).
  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  const site = useMemo(
    () =>
      resolveBaseTemplateSiteContent(siteSettings, siteSettingsLoading, {
        headerUrl,
        logoUrl,
        heroTitle,
        heroDescription,
        bannerImageUrl,
        footerTagline,
        brandName,
        showGallerySection,
        showTallySection,
        showSobreMi,
        sobreMi,
        bannerDeferred,
        galleryDeferred,
      }),
    [
      siteSettings,
      siteSettingsLoading,
      headerUrl,
      logoUrl,
      heroTitle,
      heroDescription,
      bannerImageUrl,
      footerTagline,
      brandName,
      showGallerySection,
      showTallySection,
      showSobreMi,
      sobreMi,
      bannerDeferred,
      galleryDeferred,
    ],
  );

  // Home #portfolio: site-settings curadas; fallback legacy mediaItems en landings demo.
  const activeGalleryMedia = useMemo(
    () =>
      resolveBaseTemplateGalleryPhotos(
        site.gallerySettings,
        _mediaItems,
        { shuffleGallery: true },
      ),
    [
      site.gallerySettings.galleryImageUrls?.join(","),
      site.gallerySettings.alternativeGalleryEnabled,
      site.gallerySettings.alternativeGalleryUrls?.join(","),
      site.gallerySettings.logoUrl,
      _mediaItems,
    ],
  );

  const hasHomeGallery = activeGalleryMedia.length > 0;
  const showHomeGallerySection = site.showGallerySection && hasHomeGallery;

  /** ON + fotos curadas: «Galería» hace scroll a #portfolio en la home. */
  const showGalleryScrollNav =
    showHomeGallerySection && (showGalleryNavLink ?? true);
  /** OFF o sin fotos curadas: «Galería» → /portfolio si hay portfolio público. */
  const showGalleryPortfolioNav =
    (!site.showGallerySection || !hasHomeGallery) && showPortfolioNavLink;
  /** When public portfolio is enabled (toggle on) and we have home gallery scroll, also show dedicated Portfolio link in top nav. */
  const showPortfolioLinkInNav = showPortfolioNavLink && showGalleryScrollNav;
  const { language } = useLanguage();
  const { t } = useTranslation();
  const templateConfig = useMemo(() => getServerTemplateConfig(), []);
  const countryCode = templateConfig?.country;

  // Hero CTA Override: site-settings (API) > Worker KV > geo flags. Misma prioridad que Index.tsx.
  const effectiveHeroCta = useMemo(() => {
    if (heroCta) return heroCta;
    const override = resolveHeroCtaForBaseTemplate(
      siteSettings,
      countryCode,
      templateConfig?.heroCta,
    );
    if (!override) return undefined;
    if (override.type === "membership") {
      return (
        <BaseTemplateHeroCtaMembershipMux
          override={override}
          countryCode={countryCode}
          templateName="BaseTemplate"
          workerLanguage={language}
        />
      );
    }
    return (
      <BaseTemplateHeroCtaOverrideLink
        override={override}
        countryCode={countryCode}
        templateName="BaseTemplate"
      />
    );
  }, [heroCta, siteSettings, countryCode, templateConfig?.heroCta, language]);

  useImageProtection();

  const initialHideCta = !!templateConfig?.hideCta;
  const initialShowCommunity = !!templateConfig?.showCommunity;
  const initialShowBirthday = !!templateConfig?.showBirthday;
  const initialShowWishlist = !!templateConfig?.showWishlist;
  const initialShowCheckout = !!templateConfig?.showCheckout;
  const initialShowBestFriends = !!templateConfig?.showBestFriends;
  const initialCtaUrl = templateConfig?.ctaUrl;
  const initialCtaLabel = templateConfig?.ctaLabel;
  // NOTE: templateConfig.mejoresAmigosTiers también está expuesto por el Worker
  // (para admin tooling), pero el hero render NO lo usa: la URL ganadora ya
  // viene resuelta en `ctaUrl` (admin clickeó 💰 medium o 💎 alto per-geo en
  // /admin/cta-por-region, el tier resuelto se guarda en wishlist_geo_config.url).
  const isArgentina = countryCode === "AR";
  const mpIgLang = (language?.split("-")[0] as "es" | "en" | "pt") ?? "es";
  const { interceptMercadoPagoCta, overlay: mpIgEscapeOverlay } =
    useMercadoPagoInstagramEscape({
      language: mpIgLang,
      surface: "hero_cta_mejores_amigos",
      countryCode,
    });

  // ── Always-fresh CTA loading (audit 2026-06-14) ───────────────────────────
  // Política operador: el hero CTA SIEMPRE refleja la última config de la
  // DB para la geo EXACTA del visitor, sin depender del HTML que el browser
  // pudo haber cacheado de una versión vieja del Worker.
  //
  // Estrategia para visitors AR:
  //   1. Si el HTML ya trae un flag positivo (showCheckout/showCommunity/etc.),
  //      lo renderizamos INMEDIATO (cero latencia para HTML fresco). En
  //      paralelo, hacemos un fetch background a /api/cta-flags y re-aplicamos
  //      si la respuesta difiere — el operador acaba de guardar y querés que
  //      el visitor abierto en otra pestaña vea el cambio sin reload manual.
  //   2. Si el HTML NO trae ningún flag positivo (estado "suspicious empty",
  //      típico de HTML stale o de una geo sin override), mostramos skeleton
  //      en la posición del CTA hasta que el fetch resuelva — sin layout
  //      shift y sin renderizar un hero CTA potencialmente incorrecto.
  //
  // El fetch es targeted: usa la GEO específica del visitor (province + city
  // + neighborhood). Por eso un cambio admin en city_ar=Rosario afecta solo
  // a los visitors cuyo city resuelve a Rosario — los demás (CABA, Mendoza,
  // etc.) ni siquiera lo ven. Cache 30s del endpoint = el operador puede
  // hacer cambios cada 30s sin sobrecargar la DB.
  //
  // Sin estado AR / sin geo conocida → no se dispara nada (no es necesario).
  const mejoresAmigosImpressionSentRef = useRef(false);
  const heroPageEnterSentRef = useRef(false);
  const heroGenericImpressionSentRef = useRef<string | null>(null);

  const isCookieFallback =
    templateConfig?.detectionMethod === "cookies_fallback" &&
    Boolean(countryCode) &&
    countryCode !== "UNKNOWN";
  const isArGeoSession = !effectiveHeroCta && (isArgentina || isCookieFallback);
  const needsDefensiveCtaFetch = workerCtaNeedsDefensiveFetch(templateConfig);

  const { freshFlags, isFreshLoading } = useFreshCtaFlags({
    enabled: isArGeoSession,
    skipInitialFetch: isArGeoSession && !needsDefensiveCtaFetch,
    countryCode,
    templateConfig,
  });

  const hideCta = freshFlags?.hideCta ?? initialHideCta;
  const showBirthday = freshFlags?.showBirthday ?? initialShowBirthday;
  const showWishlist = freshFlags?.showWishlist ?? initialShowWishlist;
  const showCheckout = freshFlags?.showCheckout ?? initialShowCheckout;
  const showBestFriends = freshFlags?.showBestFriends ?? initialShowBestFriends;
  const ctaUrl = freshFlags?.ctaUrl ?? initialCtaUrl;
  const ctaLabel = freshFlags?.ctaLabel ?? initialCtaLabel;
  let showCommunity = freshFlags?.showCommunity ?? initialShowCommunity;

  // Skeleton durante el background fetch para AR. Cubre dos paths:
  //   1. "suspicious empty": HTML llegó sin ningún flag → skeleton hasta confirmar.
  //   2. "stale flag": HTML traía un flag (birthday, checkout…) pero el Worker KV
  //      puede estar hasta 60s desactualizado — el fetch fresco puede devolver un
  //      tipo distinto (e.g. birthday → community). Sin skeleton, el visitor ve
  //      el CTA incorrecto y luego un switch jarring visible. Con skeleton, espera
  //      ~100-300 ms y ve el CTA correcto de entrada, sin layout shift.
  //   Cubre cualquier estado inicial (incluso hideCta=true del HTML) para
  //   evitar el jarring pop-in cuando Worker sirve "none" pero DB dice community.
  const showCtaSkeleton =
    isArGeoSession &&
    needsDefensiveCtaFetch &&
    isFreshLoading &&
    !freshFlags &&
    !workerHtmlHasPositiveHeroCta(templateConfig);

  const mejoresAmigosCtaVisible =
    !hideCta &&
    !showCtaSkeleton &&
    !effectiveHeroCta &&
    (showBirthday || showBestFriends) &&
    Boolean(ctaUrl);
  const mejoresAmigosCtaResolved =
    freshFlags?.resolved ??
    (showBestFriends
      ? "mejores_amigos"
      : showBirthday
        ? "birthday"
        : undefined);
  const mejoresAmigosCtaGeoSource = freshFlags?.source;
  const mejoresAmigosTierUrls =
    freshFlags?.mejoresAmigosTiers ?? templateConfig?.mejoresAmigosTiers;

  const withHeroGeo = useCallback(
    (
      heroCtaType: string,
      href?: string,
      extra?: Record<string, string | number | boolean | undefined>,
    ) => ({
      cta_config_source: "cta_por_region",
      ...(freshFlags ? { cta_from_fresh_fetch: true } : {}),
      ...buildHeroCtaSessionContext({
        ctaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
        ctaGeoSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
        heroCtaType,
        href,
        isArgentina,
      }),
      ...extra,
    }),
    [
      mejoresAmigosCtaResolved,
      mejoresAmigosCtaGeoSource,
      templateConfig?.heroCtaResolved,
      templateConfig?.heroCtaSource,
      isArgentina,
      freshFlags,
    ],
  );

  useEffect(() => {
    if (!mejoresAmigosCtaVisible || !ctaUrl || mejoresAmigosImpressionSentRef.current) {
      return;
    }
    mejoresAmigosImpressionSentRef.current = true;
    trackMejoresAmigosCtaImpression({
      href: ctaUrl,
      label: t("baseTemplate.heroCtaLabelBestFriends"),
      templateName: "BaseTemplate",
      countryCode,
      showBestFriends,
      showBirthdayLegacy: showBirthday && !showBestFriends,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
      tierUrls: mejoresAmigosTierUrls,
    });
  }, [
    mejoresAmigosCtaVisible,
    ctaUrl,
    countryCode,
    showBestFriends,
    showBirthday,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    mejoresAmigosTierUrls,
    t,
  ]);

  // Defensa legacy showDual → community solo si cta-por-region no pidió ocultar el CTA.
  if (
    !hideCta &&
    freshFlags?.hideCta !== true &&
    freshFlags?.resolved !== "none" &&
    (countryCode === "AR" || countryCode === "ar") &&
    !showCheckout &&
    !showCommunity &&
    !showBirthday &&
    !showWishlist &&
    !showBestFriends &&
    templateConfig?.showDual
  ) {
    showCommunity = true;
  }

  /** CTA hero: vidrio esmerilado (frosted) — translúcido pero legible; borde claro + sombra suave, alineado al hero slate. */
  const heroCtaGlass =
    "inline-flex items-center gap-3 py-3.5 px-5 max-w-[360px] mx-auto rounded-2xl " +
    "border border-white/80 bg-white/[0.42] " +
    "backdrop-blur-2xl backdrop-saturate-150 " +
    "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.72),0_1px_2px_rgba(15,23,42,0.05),0_10px_36px_-8px_rgba(15,23,42,0.12)] " +
    "ring-1 ring-slate-900/[0.05] " +
    "transition-[background-color,box-shadow,transform,border-color] duration-300 ease-out " +
    "hover:bg-white/[0.58] hover:border-white/95 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.85),0_4px_20px_-4px_rgba(15,23,42,0.1),0_18px_44px_-12px_rgba(15,23,42,0.14)] " +
    "active:scale-[0.98]";


  // Checkout AR ("Ser Exclusivo"): VITE_MP_CHECKOUT_VIP_AR (plan VIP $49.999).
  // Community AR: hero → /invitacion-premium; el CTA VIP de esa landing va a MP.
  // resto → /checkout-channel-invite (Stripe). ctaUrl admin gana en checkout.
  const arVipMpUrl = isArgentina ? getMercadoPagoCheckoutVipArUrl() : "";
  const checkoutRoute = resolveCheckoutHeroHref({
    ctaUrl: showCheckout ? ctaUrl : undefined,
    isArgentina,
    arPrecreatedMpUrl: arVipMpUrl,
  });
  const isExternalCheckout = isExternalHeroCtaHref(checkoutRoute);
  const hasCheckoutRoute = Boolean(checkoutRoute);
  const communityRoute = resolveCommunityHeroHref(ctaUrl);
  const isExternalCommunity = isExternalHeroCtaHref(communityRoute);
  const heroCtaFlagsInput = {
    hideCta,
    showCtaSkeleton,
    hasAdminHeroCta: Boolean(effectiveHeroCta),
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    ctaUrl,
  };
  useEffect(() => {
    const activeType = resolveActiveHeroCtaType(heroCtaFlagsInput);
    if (!activeType) {
      uninstallHeroCtaEngagementWatch();
      return;
    }

    const flags = buildHeroCtaFlagsSnapshot({
      hideCta,
      showCheckout,
      showCommunity,
      showBestFriends,
      showBirthday,
      showWishlist,
      hasCheckoutRoute,
      showCtaSkeleton,
      fromFreshFetch: Boolean(freshFlags),
      ctaUrl,
      ctaLabel,
      heroCtaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
      heroCtaSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
    });

    if (!heroPageEnterSentRef.current) {
      heroPageEnterSentRef.current = true;
      postHeroCtaSlackFunnel("page_enter_hero_landing", {
        templateName: "BaseTemplate",
        country: countryCode,
        heroCtaType: activeType,
        ctaResolved: mejoresAmigosCtaResolved,
        ctaGeoSource: mejoresAmigosCtaGeoSource,
        lastStep: "page_enter",
        flags,
        forceRecording: true,
        replayNearNow: true,
        extra: withHeroGeo(activeType ?? "none"),
      });
    }

    installHeroCtaEngagementWatch({
      templateName: "BaseTemplate",
      country: countryCode,
      heroCtaType: activeType,
      flags,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
    });

    return () => uninstallHeroCtaEngagementWatch();
  }, [
    hideCta,
    showCtaSkeleton,
    effectiveHeroCta,
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    countryCode,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    freshFlags,
    ctaUrl,
    ctaLabel,
    templateConfig?.heroCtaResolved,
    templateConfig?.heroCtaSource,
    withHeroGeo,
  ]);

  // Texto simple del CTA — sin emoji ni subtítulo de pasarela. El operador
  // (2026-06-14) pidió volver al texto original: solo "Ser Exclusivo".
  const checkoutCtaContent = (
    <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
      {ctaLabel || t("baseTemplate.black.heroCtaLabelCheckout", {
        defaultValue: "Ser Exclusivo",
      })}
    </span>
  );
  const checkoutCtaClick = () =>
    trackBaseTemplateClick({
      templateName: "BaseTemplate",
      elementId: "hero_cta_checkout",
      label: ctaLabel || "Ser Exclusivo",
      surface: "hero",
      href: checkoutRoute,
      countryCode,
      extra: withHeroGeo("checkout", checkoutRoute, {
        payment_provider: isArgentina ? "mercadopago" : "stripe",
        checkout_resource:
          arVipMpUrl && checkoutRoute === arVipMpUrl
            ? "mp_precreated_preapproval_plan"
            : "internal_route",
      }),
    });

  useEffect(() => {
    const activeType = resolveActiveHeroCtaType(heroCtaFlagsInput);
    if (
      !activeType ||
      activeType === "mejores_amigos" ||
      activeType === "admin_override"
    ) {
      return;
    }
    if (heroGenericImpressionSentRef.current === activeType) return;
    heroGenericImpressionSentRef.current = activeType;

    const flags = buildHeroCtaFlagsSnapshot({
      hideCta,
      showCheckout,
      showCommunity,
      showBestFriends,
      showBirthday,
      showWishlist,
      hasCheckoutRoute,
      showCtaSkeleton,
      fromFreshFetch: Boolean(freshFlags),
      ctaUrl,
      ctaLabel,
      heroCtaResolved: mejoresAmigosCtaResolved ?? templateConfig?.heroCtaResolved,
      heroCtaSource: mejoresAmigosCtaGeoSource ?? templateConfig?.heroCtaSource,
    });
    const renderKind =
      activeType === "birthday"
        ? "birthday"
        : activeType === "wishlist"
          ? "wishlist"
          : activeType === "checkout"
            ? "checkout"
            : "community";
    const label = resolveHeroCtaDisplayLabel({ kind: renderKind, t, ctaLabel });
    const href = resolveHeroCtaHrefForKind(renderKind, {
      ctaUrl,
      checkoutRoute,
    });

    postHeroCtaSlackFunnel("hero_cta_impression", {
      templateName: "BaseTemplate",
      country: countryCode,
      heroCtaType: activeType,
      label,
      href,
      ctaResolved: mejoresAmigosCtaResolved,
      ctaGeoSource: mejoresAmigosCtaGeoSource,
      lastStep: "hero_cta_impression",
      flags,
      replayNearNow: true,
      extra: withHeroGeo(activeType, href, {
        ...(activeType === "checkout"
          ? {
              payment_provider: isArgentina ? "mercadopago" : "stripe",
              checkout_resource:
                arVipMpUrl && checkoutRoute === arVipMpUrl
                  ? "mp_precreated_preapproval_plan"
                  : "internal_route",
            }
          : {}),
        ...(activeType === "wishlist" ? { cta_disabled: true, cta_reason: "proximamente" } : {}),
      }),
    });
  }, [
    hideCta,
    showCtaSkeleton,
    effectiveHeroCta,
    showCheckout,
    hasCheckoutRoute,
    showCommunity,
    showBestFriends,
    showBirthday,
    showWishlist,
    countryCode,
    checkoutRoute,
    ctaLabel,
    ctaUrl,
    freshFlags,
    isArgentina,
    arVipMpUrl,
    mejoresAmigosCtaResolved,
    mejoresAmigosCtaGeoSource,
    templateConfig?.heroCtaResolved,
    templateConfig?.heroCtaSource,
    t,
    withHeroGeo,
  ]);

  const checkoutCtaAriaLabel = t("baseTemplate.black.heroCtaAriaCheckout", {
    defaultValue: "Ser Exclusivo",
  });

  // Skeleton suave en la posición del CTA mientras el defensive fetch resuelve.
  // Mismo tamaño y radio que la pill del CTA real para evitar layout shift cuando
  // aparece. Solo aplica si estamos en el estado AR-suspicious y el fetch arrancó.
  const ctaSkeleton = (
    <div
      aria-hidden
      className={cn(
        "inline-flex items-center justify-center gap-3 py-3.5 px-5 max-w-[360px] mx-auto rounded-2xl",
        "border border-white/40 bg-white/20 backdrop-blur-2xl",
        "animate-pulse",
        "w-[260px] h-[56px]",
      )}
    >
      <span className="h-2 w-32 rounded-full bg-white/60" />
    </div>
  );

  const internalCta = effectiveHeroCta ?? (
    showCtaSkeleton ? ctaSkeleton :
    hideCta ? null :
    showCheckout && hasCheckoutRoute ? (
      isExternalCheckout ? (
        // URL externa (MP hosted con preapproval_plan_id ya creado): usar <a>
        // porque <Link> de react-router no navega out-of-app. target=_self
        // para preservar back-button al landing.
        <a
          href={checkoutRoute}
          onClick={(e) => {
            checkoutCtaClick();
            if (interceptMercadoPagoCta(checkoutRoute)) {
              e.preventDefault();
            }
          }}
          className={`${heroCtaGlass} group`}
          aria-label={checkoutCtaAriaLabel}
        >
          {checkoutCtaContent}
        </a>
      ) : (
        <Link
          to={checkoutRoute}
          onClick={checkoutCtaClick}
          className={`${heroCtaGlass} group`}
          aria-label={checkoutCtaAriaLabel}
        >
          {checkoutCtaContent}
        </Link>
      )
    ) : showCommunity ? (
      <div className="flex flex-col items-center gap-3">
        {isExternalCommunity ? (
          <a
            href={communityRoute}
            onClick={() =>
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "hero_cta_community",
                label: resolveHeroCtaDisplayLabel({ kind: "community", t }),
                surface: "hero",
                href: communityRoute,
                countryCode,
                extra: withHeroGeo("community", communityRoute),
              })
            }
            className={`${heroCtaGlass} group`}
            aria-label={resolveHeroCtaAriaLabel("community", t)}
          >
            <span className="text-2xl flex-shrink-0 drop-shadow-sm" role="img" aria-hidden>✨</span>
            <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
              {resolveHeroCtaDisplayLabel({ kind: "community", t })}
            </span>
          </a>
        ) : (
          <Link
            to={communityRoute}
            onClick={() =>
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "hero_cta_community",
                label: resolveHeroCtaDisplayLabel({ kind: "community", t }),
                surface: "hero",
                href: communityRoute,
                countryCode,
                extra: withHeroGeo("community", communityRoute),
              })
            }
            className={`${heroCtaGlass} group`}
            aria-label={resolveHeroCtaAriaLabel("community", t)}
          >
            <span className="text-2xl flex-shrink-0 drop-shadow-sm" role="img" aria-hidden>✨</span>
            <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
              {resolveHeroCtaDisplayLabel({ kind: "community", t })}
            </span>
          </Link>
        )}
        {/* Botón secundario: checkout directo — solo si hay ruta de pago disponible */}
        {hasCheckoutRoute && (
          isExternalCheckout ? (
            <a
              href={checkoutRoute}
              onClick={checkoutCtaClick}
              className={`${heroCtaGlass} group opacity-80 hover:opacity-100`}
              aria-label={checkoutCtaAriaLabel}
            >
              {checkoutCtaContent}
            </a>
          ) : (
            <Link
              to={checkoutRoute}
              onClick={checkoutCtaClick}
              className={`${heroCtaGlass} group opacity-80 hover:opacity-100`}
              aria-label={checkoutCtaAriaLabel}
            >
              {checkoutCtaContent}
            </Link>
          )
        )}
      </div>
    ) : showBirthday && !ctaUrl ? (
      <Link
        to={resolveBirthdayHeroHref()}
        onClick={() =>
          trackBaseTemplateClick({
            templateName: "BaseTemplate",
            elementId: "hero_cta_birthday",
            label: resolveHeroCtaDisplayLabel({ kind: "birthday", t }),
            surface: "hero",
            href: resolveBirthdayHeroHref(),
            countryCode,
            extra: withHeroGeo("birthday", resolveBirthdayHeroHref()),
          })
        }
        className={`${heroCtaGlass} group`}
        aria-label={resolveHeroCtaAriaLabel("birthday", t)}
        data-attr="hero_cta_birthday"
      >
        <span className="text-2xl flex-shrink-0 drop-shadow-sm" role="img" aria-hidden>🎂</span>
        <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
          {resolveHeroCtaDisplayLabel({ kind: "birthday", t })}
        </span>
      </Link>
    ) : (showBirthday || showBestFriends) && ctaUrl ? (
      // Mejores Amigos — UN botón con label "Mejores Amigos" + logo IG.
      //
      // NOTA OPERADOR (2026-06-20): tanto `showBirthday` como `showBestFriends`
      // rendean este bloque. Razón: cta_type=birthday se remapeó semánticamente
      // a Mejores Amigos (mismo flow MP checkout admin-configurado). Las
      // entries legacy con cta_type='birthday' en wishlist_geo_config siguen
      // sirviéndose pero ahora visualmente convergen con cta_type='mejores_amigos'.
      // El /r/cumple route sigue existiendo para links externos directos.
      //
      // FUENTE ÚNICA DE VERDAD: la URL MP la elige el admin per-geo en
      // /admin/cta-por-region clickeando "💰 Precio Medio" o "💎 Precio Alto"
      // (los URLs viven en mejores_amigos_price_config). El tier seleccionado
      // se guarda como url crudo en wishlist_geo_config.url para ese geo, y
      // viaja a este componente vía `ctaUrl`. Si el geo no tiene url propio,
      // el backend (Worker /api/cta-flags + Vercel fallback) cae al tier medium
      // por defecto.
      //
      // El visitor SIEMPRE ve "Mejores Amigos" como label (i18n key
      // heroCtaLabelBestFriends) — NUNCA "Precio Medio"/"Alto" (son nombres
      // internos de admin). Por eso ignoramos `ctaLabel` del API.
      (() => {
        const isExternalBestFriends = /^https?:\/\//i.test(ctaUrl);
        const trackClick = () =>
          trackMejoresAmigosCtaClick({
            source: "hero_mejores_amigos_base_template",
            templateName: "BaseTemplate",
            elementId: "hero_cta_mejores_amigos",
            label: t("baseTemplate.heroCtaLabelBestFriends"),
            surface: "hero",
            href: ctaUrl,
            countryCode,
            showBestFriends,
            showBirthdayLegacy: showBirthday && !showBestFriends,
            ctaResolved: mejoresAmigosCtaResolved,
            ctaGeoSource: mejoresAmigosCtaGeoSource,
            tierUrls: mejoresAmigosTierUrls,
          });
        const content = (
          <>
            <img
              src="https://res.cloudinary.com/nm-creators/image/upload/v1781986599/Instagram_logo_2016.svg_l1fahg.png"
              alt="Instagram"
              className="w-7 h-7 flex-shrink-0 rounded-md object-contain"
            />
            <div className="flex flex-col gap-0.5 min-w-0 text-left">
              <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
                {t("baseTemplate.heroCtaLabelBestFriends")}
              </span>
              <span className="text-[13px] font-normal text-slate-600 leading-tight">
                {t("baseTemplate.bestfriendInstagram.subtitle")}
              </span>
            </div>
          </>
        );
        return isExternalBestFriends ? (
          <a
            href={ctaUrl}
            onClick={(e) => {
              trackClick();
              if (interceptMercadoPagoCta(ctaUrl)) {
                e.preventDefault();
              }
            }}
            className={heroCtaGlass}
            aria-label={t("baseTemplate.heroCtaLabelBestFriends")}
            data-attr="hero_cta_mejores_amigos"
          >
            {content}
          </a>
        ) : (
          <Link
            to={ctaUrl}
            onClick={trackClick}
            className={heroCtaGlass}
            aria-label={t("baseTemplate.heroCtaLabelBestFriends")}
            data-attr="hero_cta_mejores_amigos"
          >
            {content}
          </Link>
        );
      })()
    ) : showWishlist ? (
      <span
        role="link"
        aria-disabled="true"
        title="Próximamente"
        className={`${heroCtaGlass} opacity-50 cursor-not-allowed pointer-events-none grayscale`}
        aria-label={t("baseTemplate.black.heroCtaAriaWishlist")}
      >
        <span className="text-2xl flex-shrink-0 drop-shadow-sm" role="img" aria-label="Regalo">🎁</span>
        <span className="text-[15px] font-semibold text-slate-900 leading-tight tracking-tight drop-shadow-[0_1px_0_rgba(255,255,255,0.55)]">
          {t("baseTemplate.black.heroCtaLabelWishlist")}
        </span>
      </span>
    ) : null
  );

  const heroTitleForDisplay = site.heroTitle;
  const heroDescriptionForDisplay = site.heroDescription;
  // Edad de la artista: desde site-settings inyectado por el Worker (importante para detección detallada).
  // Se usa SOLO en footer (no en hero/header). Respetada si está completa. El worker provee
  // brandAge vía __TEMPLATE_CONFIG__ para first paint sin fetch.
  const artistAge = (site.brandAge || "").trim();

  const resolveMediaSrc = (src: string | undefined) =>
    resolveLogoUrl(src) ?? src ?? "";
  const galleryUseWatermark = isCloudinaryWatermarkConfigured();

  const [selectedMedia, setSelectedMedia] =
    useState<BaseTemplateGalleryMediaItem | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-hide del link de gallery si no hay contenido para mostrar — la nav
  // no debe ofrecer destinos vacíos. La prop showGalleryNavLink permite override
  // explícito desde la página padre. Patrón consistente con showPortfolioNavLink.
  const effectiveShowContactNavLink = showContactNavLink && site.showTallySection;

  // Respeta la preferencia de "reducir movimiento" del sistema (accesibilidad).
  const prefersReducedMotion = useReducedMotion();

  // Instagram browser optimizations
  const instagramOpts = getInstagramOptimizations();

  const lightboxOpenTimeRef = useRef<number | null>(null);
  const lightboxWasOpenRef = useRef(false);
  const lightboxViewedCountRef = useRef(1);
  // Excellence: restore focus to trigger element when closing lightbox
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  // Galería #portfolio: cada foto arranca invisible sobre su celda gris (espacio
  // reservado, sin layout shift) y hace fade-in al terminar de cargar — en vez de
  // "inyectarse" de golpe cuando el navegador la baja. Ver render de la sección.
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const markGalleryImageLoaded = useCallback((id: number) => {
    setLoadedImages((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  // Swipe gesture state for Instagram-like navigation (reel-style gallery)
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const SWIPE_THRESHOLD = 30; // Reduced threshold for more responsive swipes (reel-style)
  const SWIPE_VELOCITY_THRESHOLD = 0.2; // Reduced velocity threshold for smoother navigation

  // Block body scroll when lightbox is open (especially important for Instagram browser)
  useEffect(() => {
    if (selectedMedia) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      // Block scroll - especially important in Instagram browser
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedMedia, instagramOpts.isInstagramBrowser]);

  useEffect(() => {
    if (!selectedMedia) {
      lightboxWasOpenRef.current = false;
      return;
    }
    lightboxOpenTimeRef.current = Date.now();
    if (!lightboxWasOpenRef.current) {
      lightboxViewedCountRef.current = 1;
      trackLightboxOpenedPostHog(
        selectedMedia.id,
        "gallery_click",
        selectedMedia.type,
        "BaseTemplate",
        countryCode,
      );
      lightboxWasOpenRef.current = true;
    }
  }, [selectedMedia, countryCode]);

  const closeLightbox = useCallback(
    (closeKind: "x" | "tap") => {
      setSelectedMedia((current) => {
        if (!current) return null;
        const openedAt = lightboxOpenTimeRef.current;
        const timeSpent = openedAt
          ? Math.max(0, Math.round((Date.now() - openedAt) / 1000))
          : 0;
        trackLightboxClosedPostHog(
          current.id,
          timeSpent,
          lightboxViewedCountRef.current,
          "BaseTemplate",
          countryCode,
        );
        trackBaseTemplateClick({
          templateName: "BaseTemplate",
          elementId:
            closeKind === "x" ? "lightbox_close_x" : "lightbox_close_tap",
          label:
            closeKind === "x"
              ? "Lightbox close (X)"
              : "Lightbox close (tap)",
          surface: "lightbox",
          countryCode,
          extra: {
            close_kind: closeKind,
            media_id: String(current.id),
          },
        });
        lightboxOpenTimeRef.current = null;

        // Excellence a11y: restore focus
        const toFocus = lastFocusedElementRef.current;
        if (toFocus && typeof toFocus.focus === "function") {
          window.setTimeout(() => {
            try { toFocus.focus(); } catch {
              /* no-op */
            }
          }, 0);
        }
        lastFocusedElementRef.current = null;

        return null;
      });
    },
    [countryCode],
  );

  // Navigation helpers for lightbox
  const navigateToPrevious = () => {
    if (!selectedMedia || activeGalleryMedia.length === 0) return;
    const currentIndex = activeGalleryMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const prevIndex =
      currentIndex > 0 ? currentIndex - 1 : activeGalleryMedia.length - 1;
    const prevItem = activeGalleryMedia[prevIndex];
    trackLightboxNavigationPostHog(
      "prev",
      selectedMedia.id,
      prevItem.id,
      "BaseTemplate",
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(prevItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  const navigateToNext = () => {
    if (!selectedMedia || activeGalleryMedia.length === 0) return;
    const currentIndex = activeGalleryMedia.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const nextIndex =
      currentIndex < activeGalleryMedia.length - 1 ? currentIndex + 1 : 0;
    const nextItem = activeGalleryMedia[nextIndex];
    trackLightboxNavigationPostHog(
      "next",
      selectedMedia.id,
      nextItem.id,
      "BaseTemplate",
      countryCode,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(nextItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  // Gallery grid styles are now in index.css (static) for better initial load performance.
  // No more runtime style injection.

  // Analytics + Instagram browser optimizations
  useEffect(() => {
    const tabSessionId = getOrCreateTabSessionId(POSTHOG_BASE_TEMPLATE_TAB_SESSION_KEY);
    const featureList = [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ];
    registerBaseTemplateSessionContextPostHog(tabSessionId, {
      templateSurface: "BaseTemplate",
      countryCode: countryCode,
      isInstagramInApp: instagramOpts.isInstagramBrowser,
    });
    trackBaseTemplateFunnelSessionPostHog({
      tabSessionId,
      templateSurface: "BaseTemplate",
      countryCode: countryCode,
      isInstagramInApp: instagramOpts.isInstagramBrowser,
      features: featureList,
    });

    trackTemplateUsageGA("BaseTemplate", "LATAM", [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ]);
    trackTemplateUsagePostHog("BaseTemplate", "LATAM", [
      "uniform_gallery",
      "mobile_first_responsive",
      "shuffle_functionality",
      ...(instagramOpts.isInstagramBrowser
        ? ["instagram_browser_optimized"]
        : []),
    ]);

    // Facebook Pixel: Track ViewContent
    trackViewContent("Base Template Page", "page", {
      template: "BaseTemplate",
      region: "LATAM",
      is_instagram_browser: instagramOpts.isInstagramBrowser,
    });

    applyInstagramBrowserCSS();
    return () => {
      unregisterBaseTemplateSessionContextPostHog();
    };
  }, [instagramOpts.isInstagramBrowser, countryCode]);

  // ── Framer Motion: variantes reutilizables (se neutralizan si reduce-motion) ──
  const ease = [0.22, 1, 0.36, 1] as const; // easeOutExpo suave (estándar 2026)
  const heroContainer = {
    hidden: {},
    show: {
      transition: prefersReducedMotion
        ? {}
        : { staggerChildren: 0.12, delayChildren: 0.05 },
    },
  };
  const heroItem = prefersReducedMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
        show: {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          transition: { duration: 0.7, ease },
        },
      };
  const sectionReveal = prefersReducedMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 28 },
        show: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
      };
  const galleryContainer = {
    hidden: {},
    show: {
      transition: prefersReducedMotion ? {} : { staggerChildren: 0.06 },
    },
  };
  const galleryItem = prefersReducedMotion
    ? { hidden: { opacity: 1 }, show: { opacity: 1 } }
    : {
        hidden: { opacity: 0, y: 24, scale: 0.96 },
        show: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: { duration: 0.5, ease },
        },
      };

  return (
    <div
      className={`min-h-screen bg-white text-black ${instagramOpts.cssClasses.container}`}
    >
      {/* Header - Light Elegant Design */}
      <header className="sticky top-0 z-50 w-full border-b border-black/10 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top,0px)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 min-h-[44px]">
            {/* Logo - image from Cloudinary/Cloudflare or text fallback */}
            <div className="flex-shrink-0">
              <a
                href={site.headerUrl}
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "header_logo",
                    label: "Header logo",
                    surface: "header",
                    href: site.headerUrl,
                    countryCode,
                  })
                }
                className="inline-flex items-center gap-2.5 text-lg sm:text-xl font-display font-medium text-black whitespace-nowrap tracking-wider hover:opacity-80 transition-opacity"
                aria-label={site.brandName}
              >
                {site.logoDisplaySrc ? (
                  <img
                    src={site.logoDisplaySrc}
                    alt={site.brandName}
                    className="h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover ring-1 ring-black/10 shrink-0"
                    width={36}
                    height={36}
                    decoding="async"
                    fetchPriority="high"
                  />
                ) : null}
                <span>{site.brandName}</span>
              </a>
            </div>

            {/* Nav + idioma + menú móvil (mismo patrón que SiteLayout) */}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
            <nav
              className="hidden md:flex space-x-5 sm:space-x-6 mr-2"
              aria-label="Main navigation"
            >
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_desktop_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_desktop",
                    href: "#home",
                    countryCode,
                  })
                }
                className="text-black font-medium transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-black/20 rounded tracking-wide text-sm"
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showGalleryPortfolioNav && (
                <Link
                  to="/portfolio"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_desktop_gallery_portfolio",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_desktop",
                      href: "/portfolio",
                      countryCode,
                    })
                  }
                  className="text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-black/20 rounded tracking-wide text-sm"
                >
                  {t("baseTemplate.nav.gallery")}
                </Link>
              )}
              {showGalleryScrollNav && (
                <a
                  href="#portfolio"
                  onClick={(e) => {
                    e.preventDefault();
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_desktop_gallery",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_desktop",
                      href: "#portfolio",
                      countryCode,
                    });
                    document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-black/20 rounded tracking-wide text-sm"
                >
                  {t("baseTemplate.nav.gallery")}
                </a>
              )}
              {showPortfolioLinkInNav && (
                <Link
                  to="/portfolio"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_desktop_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_desktop",
                      href: "/portfolio",
                      countryCode,
                    })
                  }
                  className="text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-black/20 rounded tracking-wide text-sm"
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              {effectiveShowContactNavLink && (
                <a
                  href="#contacto"
                  onClick={() =>
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_desktop_contacto",
                      label: t("baseTemplate.nav.contact"),
                      surface: "nav_desktop",
                      href: "#contacto",
                      countryCode,
                    })
                  }
                  className="text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-black/20 rounded tracking-wide text-sm"
                >
                  {t("baseTemplate.nav.contact")}
                </a>
              )}
            </nav>
            <LanguageSwitcher />
            <div className="md:hidden">
              <button
                type="button"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => {
                  const newState = !mobileMenuOpen;
                  setMobileMenuOpen(newState);
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "mobile_menu_toggle",
                    label: newState ? "Open mobile menu" : "Close mobile menu",
                    surface: "mobile_menu",
                    countryCode,
                    extra: { menu_open: newState },
                  });
                }}
                className="text-black/60 hover:text-black focus:outline-none focus:ring-2 focus:ring-black/20 rounded"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-black/10 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a
                href="#home"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "nav_mobile_home",
                    label: t("baseTemplate.nav.home"),
                    surface: "nav_mobile",
                    href: "#home",
                    countryCode,
                  })
                }
                className="block px-3 py-2 text-black font-medium tracking-wide"
              >
                {t("baseTemplate.nav.home")}
              </a>
              {showGalleryPortfolioNav && (
                <Link
                  to="/portfolio"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_mobile_gallery_portfolio",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_mobile",
                      href: "/portfolio",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-black/60 hover:text-black transition-colors font-medium tracking-wide"
                >
                  {t("baseTemplate.nav.gallery")}
                </Link>
              )}
              {showGalleryScrollNav && (
                <a
                  href="#portfolio"
                  onClick={(e) => {
                    e.preventDefault();
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_mobile_gallery",
                      label: t("baseTemplate.nav.gallery"),
                      surface: "nav_mobile",
                      href: "#portfolio",
                      countryCode,
                    });
                    document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" });
                    setMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-black/60 hover:text-black transition-colors font-medium tracking-wide"
                >
                  {t("baseTemplate.nav.gallery")}
                </a>
              )}
              {showPortfolioLinkInNav && (
                <Link
                  to="/portfolio"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_mobile_portfolio",
                      label: t("baseTemplate.nav.portfolio"),
                      surface: "nav_mobile",
                      href: "/portfolio",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-black/60 hover:text-black transition-colors font-medium tracking-wide"
                >
                  {t("baseTemplate.nav.portfolio")}
                </Link>
              )}
              {effectiveShowContactNavLink && (
                <a
                  href="#contacto"
                  onClick={() => {
                    trackBaseTemplateClick({
                      templateName: "BaseTemplate",
                      elementId: "nav_mobile_contacto",
                      label: t("baseTemplate.nav.contact"),
                      surface: "nav_mobile",
                      href: "#contacto",
                      countryCode,
                    });
                    setMobileMenuOpen(false);
                  }}
                  className="block px-3 py-2 text-black/60 hover:text-black transition-colors font-medium tracking-wide"
                >
                  {t("baseTemplate.nav.contact")}
                </a>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" className="relative">
        {/* Light Hero Section with Cloudflare Banner - Mobile first */}
        <section
          id="home"
          data-section-name="Hero"
          className="relative w-full bg-white overflow-hidden min-h-[60vh] sm:min-h-[65vh] lg:min-h-[70vh]"
        >
          {/* Banner Image: site settings (primera de galería) o Cloudflare por defecto - 100% sin overlay */}
          {/* bannerDeferred: evita flash imagen incorrecta mientras cargan site settings */}
          <div className="absolute inset-0">
            <AnimatePresence mode="wait">
              {!site.bannerDeferred && site.bannerDisplaySrc ? (
                <motion.img
                  key={site.bannerDisplaySrc}
                  src={site.bannerDisplaySrc}
                  srcSet={site.bannerSrcSet}
                  sizes="100vw"
                  alt={`${BRAND_PERSON_NAME} - Professional Content Creator and Lifestyle Influencer Banner Image`}
                  className="banner-image w-full h-full object-cover"
                  style={{ objectPosition: "center center" }}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  initial={prefersReducedMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
                  transition={{ duration: prefersReducedMotion ? 0 : 0.55, ease }}
                  onLoad={() => {
                    const fallbackBg = document.querySelector(".banner-fallback-bg");
                    if (fallbackBg) {
                      (fallbackBg as HTMLElement).style.opacity = "0";
                    }
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLElement;
                    target.style.display = "none";

                    const fallbackBg = document.querySelector(".banner-fallback-bg");
                    if (fallbackBg) {
                      (fallbackBg as HTMLElement).style.opacity = "1";
                    }

                    if (typeof window !== "undefined" && window.gtag) {
                      window.gtag("event", "image_load_error", {
                        event_category: "Cloudflare Images",
                        event_label: "banner_image_failed",
                        image_id: "9fdc1148-5ac5-48d3-2a3a-6ce149481300",
                        template: "BaseTemplate",
                        has_env_var: !!import.meta.env.VITE_CLOUDFLARE_ACCOUNT_HASH,
                      });
                    }
                  }}
                />
              ) : null}
            </AnimatePresence>
            {site.bannerDeferred ? (
              <BaseTemplateDeferredBannerOverlay
                tone="light"
                brandName={site.brandName}
                logoUrl={site.logoUrl}
                prefersReducedMotion={prefersReducedMotion}
              />
            ) : null}
          </div>

          {/* Elegant Fallback Background - visible cuando bannerDeferred, sin banner configurado, o cuando falla la imagen */}
          <div
            className={`banner-fallback-bg absolute inset-0 transition-opacity duration-500 ${site.bannerDeferred || !site.bannerRaw ? "opacity-100" : "opacity-0"}`}
            style={{
              background: `
              linear-gradient(165deg, #f8fafc 0%, #e2e8f0 42%, #cbd5e1 100%),
              radial-gradient(ellipse 120% 70% at 50% -10%, rgba(148, 163, 184, 0.28) 0%, transparent 55%),
              radial-gradient(ellipse 90% 60% at 100% 100%, rgba(226, 232, 240, 0.85) 0%, transparent 52%),
              radial-gradient(ellipse 70% 50% at 0% 80%, rgba(241, 245, 249, 0.9) 0%, transparent 48%)
            `,
              backgroundBlendMode: "normal, normal, normal, normal",
            }}
          >
            {/* Subtle pattern overlay for texture */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                backgroundSize: "60px 60px",
              }}
            />
          </div>

          {/* Subtle Grain Texture */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='4' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundSize: "200px 200px",
            }}
          ></div>

          {/* Hero Content - Mobile first: texto un poco más arriba, sin tapar la cara */}
          <div className="relative min-h-[60vh] sm:min-h-[65vh] lg:min-h-[70vh] flex items-end justify-center px-4 pb-14 pt-16 sm:px-6 sm:pb-20 sm:pt-20 lg:px-8 lg:pb-24 lg:pt-24">
            <motion.div
              className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8 lg:space-y-10"
              variants={heroContainer}
              initial="hidden"
              animate="show"
            >
              <motion.div className="space-y-4 sm:space-y-6" variants={heroContainer}>
                {heroTitleForDisplay?.trim() && (() => {
                  const raw = heroTitleForDisplay.trim();
                  // Split por | → H1 solo nombre, subtítulo en <p> (SEO: H1 limpio)
                  const parts = raw.split(/\s*[\u007C\uFF5C\u00A6]\s*/);
                  const name = parts[0]?.trim() || raw;
                  const subtitle = parts.length >= 2 ? parts.slice(1).join(" ").trim() : "";
                  return (
                    <>
                      <motion.h1
                        variants={heroItem}
                        className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-black tracking-tight leading-tight"
                      >
                        {name}
                      </motion.h1>
                      {subtitle && (
                        <motion.p
                          variants={heroItem}
                          className="font-body text-base sm:text-xl md:text-2xl lg:text-3xl text-black/85 font-medium lining-nums tabular-nums"
                        >
                          {subtitle}
                        </motion.p>
                      )}
                    </>
                  );
                })()}
                {heroDescriptionForDisplay?.trim() && (
                  <motion.p
                    variants={heroItem}
                    className="font-body text-sm sm:text-lg md:text-xl lg:text-2xl text-black/85 max-w-2xl sm:max-w-3xl mx-auto leading-relaxed font-light lining-nums tabular-nums px-2 sm:px-0"
                  >
                    {heroDescriptionForDisplay.trim()}
                  </motion.p>
                )}
              </motion.div>

              {/* CTA hero: Worker (__TEMPLATE_CONFIG__) u heroCta manual */}
              {internalCta && (
                <motion.div variants={heroItem}>
                  {internalCta}
                </motion.div>
              )}

              {/* Social Links - Removed (Instagram and YouTube) */}
            </motion.div>
          </div>
        </section>

        {/* Sobre mí - versión corta, link a /sobre-mi para el texto completo */}
        {showSobreMiOnHome !== false && site.sobreMi?.trim() && (
          <section
            id="sobre-mi-preview"
            className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-white"
          >
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-lg sm:text-xl md:text-2xl font-display font-bold text-black mb-4 tracking-wide">
                {t("baseTemplate.about.sectionTitle")}
              </h2>
              <p className="text-black/80 text-sm sm:text-base leading-relaxed mb-4">
                {site.sobreMi.length > 180
                  ? `${site.sobreMi.slice(0, 180).trim()}…`
                  : site.sobreMi}
              </p>
              <Link
                to="/sobre-mi"
                onClick={() =>
                  trackBaseTemplateClick({
                    templateName: "BaseTemplate",
                    elementId: "sobre_mi_read_more",
                    label: t("baseTemplate.about.readMore"),
                    surface: "sobre_mi",
                    href: "/sobre-mi",
                    countryCode,
                  })
                }
                className="text-sm font-medium text-black/70 hover:text-black underline underline-offset-2"
              >
                {t("baseTemplate.about.readMore")}
              </Link>
            </div>
          </section>
        )}

        {/* Portfolio/Gallery Section - Light style */}
        {showHomeGallerySection && (
        <section
          id="portfolio"
          data-section-name="Gallery"
          className="py-6 px-2 sm:py-12 sm:px-4 md:py-16 md:px-6 lg:py-20 lg:px-8 bg-gray-50"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-4 sm:mb-8 md:mb-12"
              variants={sectionReveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-10%" }}
            >
              <h2 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-display font-bold text-black mb-2 sm:mb-4 tracking-wide">
                {t("baseTemplate.gallery.title")}
              </h2>
              {/* Gallery automatically shuffles on load - no manual button needed */}
            </motion.div>

            {/* Gallery: uniform grid - formal, consistent layout. No mostrar hasta tener site settings. */}
            <AnimatePresence mode="wait">
            {site.galleryDeferred ? (
              <motion.div
                key="gallery-loader"
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                transition={{ duration: prefersReducedMotion ? 0 : 0.3, ease }}
              >
                <BaseTemplateDeferredGallery
                  tone="light"
                  brandName={site.brandName}
                  logoUrl={site.logoUrl}
                  label={t("baseTemplate.gallery.loading")}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </motion.div>
            ) : (() => {
              const displayMedia = activeGalleryMedia
                .filter((item) => !failedImages.has(item.id))
                .slice(0, 9);
              return (
            <motion.div
              key="gallery-content"
              className={`gallery-grid-uniform mb-6 sm:mb-12 ${instagramOpts.cssClasses.gallery}`}
              variants={galleryContainer}
              initial="hidden"
              animate="show"
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.35, ease }}
            >
              {displayMedia.map((item, index) => (
                  <motion.div
                    key={item.id}
                    variants={galleryItem}
                    className={`gallery-grid-item relative bg-gray-100 overflow-hidden touch-manipulation cursor-pointer ${index === 8 ? "hidden sm:block" : ""}`}
                  >
                    {/* Simple click to open - No hover arrows, super user friendly */}
                    <button
                      type="button"
                      onClick={() => {
                        trackMediaItemClickPostHog(
                          item.id,
                          item.type,
                          item.title,
                          index,
                          "BaseTemplate",
                          countryCode,
                        );
                        lastFocusedElementRef.current = document.activeElement as HTMLElement | null;
                        setSelectedMedia(item);
                      }}
                      aria-label={t("baseTemplate.galleryAlt.viewAria", {
                        type:
                          item.title ||
                          t("baseTemplate.galleryAlt.contentFallback"),
                      })}
                      className="w-full h-full focus:outline-none focus:ring-2 focus:ring-foreground/20 active:opacity-90"
                    >
                      <img
                        src={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? getCloudinaryUrl(resolveMediaSrc(item.src), { watermark: galleryUseWatermark })
                            : item.src
                        }
                        srcSet={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? `${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 400, watermark: galleryUseWatermark })} 400w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 800, watermark: galleryUseWatermark })} 800w, ${getCloudinaryUrl(resolveMediaSrc(item.src), { width: 1200, watermark: galleryUseWatermark })} 1200w`
                            : undefined
                        }
                        sizes={
                          resolveMediaSrc(item.src).includes("res.cloudinary.com")
                            ? "(max-width: 639px) 50vw, (max-width: 1023px) 33vw, (max-width: 1535px) 25vw, 20vw"
                            : undefined
                        }
                        alt={`${item.title ? `${item.title} — ` : ""}${t("baseTemplate.galleryAlt.photoSuffix")}`}
                        className={`w-full h-full object-cover transition-opacity ease-out ${prefersReducedMotion ? "duration-0" : "duration-700"} ${loadedImages.has(item.id) ? "opacity-100" : "opacity-0"}`}
                        // Primeras 2 fotos eager+prioridad: acortan la ventana en que
                        // la galería aparece "vacía". El resto lazy (perf below-fold).
                        loading={index < 2 ? "eager" : "lazy"}
                        fetchPriority={index < 2 ? "high" : "auto"}
                        decoding="async"
                        onLoad={() => markGalleryImageLoaded(item.id)}
                        onError={(e) => {
                          // Hide broken images silently - don't show in gallery
                          setFailedImages((prev) => new Set(prev).add(item.id));
                          const target = e.target as HTMLImageElement;
                          if (target) {
                            target.style.display = "none";
                          }
                        }}
                      />
                    </button>
                  </motion.div>
                ))}
            </motion.div>
              );
            })()}
            </AnimatePresence>

            {/* CTA buttons removed - BaseTemplate should only have gallery and social links */}
          </div>
        </section>
        )}

        {/* Contact — Tally @ consulta.delfuocampo.com → webhook /api/tally-webhook */}
        {site.showTallySection && (
          <BaseTemplateTallyContact
            countryCode={countryCode}
            templateName="BaseTemplate"
            variant="light"
          />
        )}
      </main>

      <BaseTemplateFooter
        variant="light"
        language={language}
        contactHref={effectiveShowContactNavLink ? "#contacto" : undefined}
        analyticsTemplate="BaseTemplate"
        countryCode={countryCode}
        brandName={site.brandName}
        footerTagline={site.footerTagline}
        brandAge={artistAge}
        showPortfolioNavLink={showPortfolioNavLink}
        showSobreMiNavLink={site.showSobreMi}
      />

      {mpIgEscapeOverlay}

      {/* Reel-style Lightbox Modal - Instagram-like vertical swipe experience */}
      <AnimatePresence>
      {selectedMedia && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.22, ease }}
          role="dialog"
          aria-modal="true"
          aria-label={t("baseTemplate.lightbox.ariaLabel", { defaultValue: "Galería" })}
          className={`fixed inset-0 z-50 bg-white ${
            instagramOpts.cssClasses.modal || ""
          }`}
          style={{
            touchAction: "none",
          }}
          onClick={(e) => {
            // Excellence: close on backdrop click (only if directly on the overlay)
            if (e.target === e.currentTarget) {
              closeLightbox("tap");
            }
          }}
        >
          {/* Close Button - Safe area aware */}
          <button
            type="button"
            onClick={() => closeLightbox("x")}
            className="absolute w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/90 hover:text-black bg-white/60 rounded-full hover:bg-white/80 ui-transition duration-200 z-30 focus:outline-none focus:ring-2 focus:ring-black/50 touch-manipulation active:scale-95"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
              right: "max(1rem, env(safe-area-inset-right, 0px))",
            }}
            aria-label={t("baseTemplate.lightbox.close")}
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>

          {/* Media counter indicator */}
          <div
            className="absolute left-1/2 -translate-x-1/2 z-30 px-3 py-1.5 bg-white/60 rounded-full text-black/80 text-xs font-medium tracking-wide tabular-nums lining-nums"
            style={{
              top: "max(1rem, env(safe-area-inset-top, 0px))",
            }}
          >
            {activeGalleryMedia.findIndex((item) => item.id === selectedMedia.id) +
              1}{" "}
            /{" "}
            {activeGalleryMedia.filter((item) => !failedImages.has(item.id)).length}
          </div>

          {/* Navigation buttons - visibles en mobile y desktop */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "lightbox_nav_prev",
                label: "Lightbox previous",
                surface: "lightbox",
                countryCode,
              });
              navigateToPrevious();
            }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/60 hover:text-black/90 bg-white/50 rounded-full hover:bg-white/70 ui-transition duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-black/30 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.previous")}
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              trackBaseTemplateClick({
                templateName: "BaseTemplate",
                elementId: "lightbox_nav_next",
                label: "Lightbox next",
                surface: "lightbox",
                countryCode,
              });
              navigateToNext();
            }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center text-black/60 hover:text-black/90 bg-white/50 rounded-full hover:bg-white/70 ui-transition duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-black/30 touch-manipulation active:scale-95"
            aria-label={t("baseTemplate.lightbox.next")}
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2} />
          </button>

          {/* Reel Content - Full screen swipeable */}
          <div
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            style={{
              height: "100dvh",
              WebkitOverflowScrolling: "touch",
            }}
            onTouchStart={(e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                return;
              }
              const touch = e.touches[0];
              swipeStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              };
            }}
            onTouchMove={(e) => {
              if (swipeStartRef.current) {
                const touch = e.touches[0];
                const deltaX = Math.abs(
                  touch.clientX - swipeStartRef.current.x,
                );
                const deltaY = Math.abs(
                  touch.clientY - swipeStartRef.current.y,
                );
                // Prevent default to allow smooth swipe animation
                if (deltaX > 10 || deltaY > 10) {
                  e.preventDefault();
                }
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeStartRef.current) return;

              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                swipeStartRef.current = null;
                return;
              }

              const touch = e.changedTouches[0];
              const deltaX = touch.clientX - swipeStartRef.current.x;
              const deltaY = touch.clientY - swipeStartRef.current.y;
              const deltaTime = Date.now() - swipeStartRef.current.time;
              const velocityX = Math.abs(deltaX) / deltaTime;
              const velocityY = Math.abs(deltaY) / deltaTime;

              const swipeThreshold = instagramOpts.isInstagramBrowser ? 20 : 30;
              const velocityThreshold = 0.15;

              // Vertical swipe - Instagram reel style (up = next, down = previous)
              if (
                Math.abs(deltaY) > Math.abs(deltaX) &&
                Math.abs(deltaY) > swipeThreshold &&
                velocityY > velocityThreshold
              ) {
                if (deltaY < 0) {
                  // Swipe up - next item
                  navigateToNext();
                } else {
                  // Swipe down - previous item
                  navigateToPrevious();
                }
              }
              // Horizontal swipe - alternative navigation
              else if (
                Math.abs(deltaX) > Math.abs(deltaY) &&
                Math.abs(deltaX) > swipeThreshold &&
                velocityX > velocityThreshold
              ) {
                if (deltaX > 0) {
                  navigateToPrevious();
                } else {
                  navigateToNext();
                }
              }
              // Tap to close (if minimal movement)
              else if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
                closeLightbox("tap");
              }

              swipeStartRef.current = null;
            }}
          >
            {/* Animated content wrapper */}
            <AnimatePresence mode="wait">
            <motion.div
              className="w-full h-full flex items-center justify-center"
              key={selectedMedia.id}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -18 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.25, ease }}
            >
              <div className="relative w-full h-full flex items-center justify-center bg-white px-2">
                <img
                  key={selectedMedia.id}
                  src={
                    resolveMediaSrc(selectedMedia.src).includes("res.cloudinary.com")
                      ? getCloudinaryUrl(resolveMediaSrc(selectedMedia.src), {
                          width: 1600,
                          watermark: galleryUseWatermark,
                        })
                      : selectedMedia.src
                  }
                  alt={`${selectedMedia.title ? `${selectedMedia.title} — ` : ""}${t("baseTemplate.galleryAlt.photoSuffix")}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  style={{
                    maxHeight: "85vh",
                  }}
                />
              </div>
            </motion.div>
            </AnimatePresence>

            {/* Hint mobile: desliza o flechas para cambiar, toca para cerrar */}
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-black/50 pointer-events-none md:hidden"
              style={{
                paddingBottom: "env(safe-area-inset-bottom, 0px)",
              }}
            >
              <span className="text-xs tracking-wide">
                {t("baseTemplate.lightbox.swipeHint")}
              </span>
            </div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

// ── Hero CTA admin override (site-settings / Worker KV) ───────────────────────

export function BaseTemplateHeroCtaOverrideLink({
  override,
  countryCode,
  templateName,
}: {
  override: ResolvedHeroCtaOverride;
  countryCode?: string;
  templateName: "BaseTemplate" | "BaseTemplateBlack";
}) {
  const isExternal = override.url.startsWith("http");
  return (
    <a
      href={override.url}
      rel={isExternal ? "noopener noreferrer" : undefined}
      target={isExternal ? "_blank" : undefined}
      data-attr="hero_cta_admin_override"
      data-cta-source="admin_site_settings"
      data-cta-type={override.type}
      onClick={() => {
        trackBaseTemplateClick({
          templateName,
          elementId: "hero_cta_admin_override",
          label: override.label,
          surface: "hero",
          href: override.url,
          countryCode,
          extra: {
            hero_cta_type: override.type,
            cta_source: "admin_site_settings",
          },
        });
      }}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold ui-transition bg-white/90 text-black backdrop-blur-md border border-white/40 hover:bg-white hover:shadow-lg"
    >
      {override.emoji ? <span aria-hidden>{override.emoji}</span> : null}
      <span>{override.label}</span>
    </a>
  );
}

// ── Carga diferida (site-settings): marca + skeleton alineados a admin ────────

type BaseTemplateDeferredProps = {
  tone: BaseTemplateDeferredUiTone;
  brandName: string;
  logoUrl?: string;
  prefersReducedMotion: boolean | null;
};

function BaseTemplateDeferredMark({
  tone,
  brandName,
  logoUrl,
  prefersReducedMotion,
}: BaseTemplateDeferredProps) {
  const ui = baseTemplateDeferredUi[tone];
  const resolvedLogo = toCloudinaryLogoDelivery(logoUrl, 32);
  const monogram = (brandName.trim()[0] ?? BRAND_NAME.trim()[0] ?? "•").toUpperCase();

  return (
    <div className="relative mx-auto h-12 w-12 shrink-0">
      {prefersReducedMotion ? (
        <span
          aria-hidden
          className={cn("absolute inset-0 rounded-full border-2", ui.ring)}
        />
      ) : (
        <>
          {/* 2 unified subtle floating rings (identical for mobile+desktop, subtle pulse, visible border color) */}
          <motion.span
            aria-hidden
            className={cn("absolute inset-0 rounded-full border-2", ui.ring)}
            animate={{ scale: [1, 1.03, 1], opacity: [0.25, 0.45, 0.25] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "center center" }}
          />
          <motion.span
            aria-hidden
            className={cn("absolute inset-[6px] rounded-full border-2", ui.ring)}
            animate={{ scale: [1, 1.03, 1], opacity: [0.25, 0.45, 0.25] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
            style={{ transformOrigin: "center center" }}
          />
        </>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        {resolvedLogo ? (
          <img
            src={resolvedLogo}
            alt={brandName}
            className="h-8 w-8 rounded-full object-cover ring-1 ring-inset ring-black/10"
            width={32}
            height={32}
            decoding="async"
          />
        ) : (
          <span
            aria-hidden
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full text-sm font-display font-semibold tracking-tight",
              ui.monogramBg,
              ui.mark,
            )}
          >
            {monogram}
          </span>
        )}
      </div>
    </div>
  );
}

export function BaseTemplateDeferredBannerOverlay(props: BaseTemplateDeferredProps) {
  const ui = baseTemplateDeferredUi[props.tone];
  return (
    <div className={ui.overlay} aria-hidden>
      <BaseTemplateDeferredMark {...props} />
    </div>
  );
}

export function BaseTemplateDeferredGallery({
  label,
  ...markProps
}: BaseTemplateDeferredProps & { label: string }) {
  const ui = baseTemplateDeferredUi[markProps.tone];
  const { prefersReducedMotion } = markProps;

  return (
    <div className="w-full" aria-busy="true" aria-live="polite" aria-label={label}>
      <div className="mb-6 sm:mb-10 flex flex-col items-center gap-3">
        <div role="status">
          <BaseTemplateDeferredMark {...markProps} />
        </div>
        <p className={cn("text-xs sm:text-sm font-medium tracking-wide", ui.label)}>
          {label}
        </p>
      </div>
      <div className="gallery-grid-uniform mb-6 sm:mb-12" aria-hidden>
        {Array.from({ length: 9 }, (_, index) => (
          <div
            key={index}
            className={cn(
              "gallery-grid-item relative overflow-hidden",
              ui.skeleton,
              index === 8 ? "hidden sm:block" : "",
            )}
          >
            {!prefersReducedMotion && (
              <motion.div
                className={cn(
                  "absolute inset-0 bg-gradient-to-r from-transparent to-transparent",
                  ui.shimmer,
                )}
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{
                  duration: 1.6,
                  repeat: Infinity,
                  ease: "linear",
                  repeatDelay: 0.35,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
